/**
 * Eagerly loads a document's page rasters before printing.
 *
 * Workspace documents render one `<img>` per page, marked
 * `loading="lazy" decoding="async"` so that opening a 134-page document does
 * not fetch and decode every page up front (LAR-252). `window.print()` is
 * synchronous: the browser un-lazies those images for print layout but does not
 * wait for the fetches it just started, so every page the reader had not
 * scrolled past printed blank — 3 pages of content and 131 empty sheets.
 *
 * Awaiting the rasters here, before print, is the only lever the page has. On a
 * 134-page document that is ~14MB and about three seconds; the caller is
 * expected to show progress while it runs.
 */
export const PRINT_IMAGE_LOAD_TIMEOUT_MS = 30_000;

export interface PrintImagePreloadResult {
  /** Every `<img>` found under the root. */
  total: number;
  /** Already decoded when printing started — these cost nothing. */
  alreadyLoaded: number;
  /** Awaited, and how many arrived. */
  pending: number;
  loaded: number;
  /** Rasters that errored: their pages will print blank. */
  failed: number;
  /** True when the timeout fired before every raster settled. */
  timedOut: boolean;
}

interface PreloadOptions {
  /**
   * Called once, synchronously, with the number of rasters about to be awaited
   * — before the first one is awaited, so a caller can put progress on screen
   * for the whole wait. Not called when there is nothing to fetch.
   */
  onStart?: (pending: number) => void;
  onProgress?: (settled: number, pending: number) => void;
  timeoutMs?: number;
}

const isLoaded = (img: HTMLImageElement) =>
  img.complete && img.naturalWidth > 0;

export const preloadDocumentImagesForPrint = async (
  root: ParentNode | null,
  {
    onStart,
    onProgress,
    timeoutMs = PRINT_IMAGE_LOAD_TIMEOUT_MS,
  }: PreloadOptions = {}
): Promise<PrintImagePreloadResult> => {
  const images = root
    ? Array.from(root.querySelectorAll<HTMLImageElement>("img"))
    : [];

  // Take everything off lazy loading first: the browser starts fetching as soon
  // as the attribute changes, so all of them are in flight before we await any.
  // `decoding: sync` drops the async-decode deferral, which would otherwise let
  // print snapshot a loaded-but-undecoded raster. Both are left in place
  // afterwards — nothing needs restoring once the images are loaded, which is
  // what makes this safe to do around a blocking window.print() (LAR-371).
  const pending: HTMLImageElement[] = [];
  for (const img of images) {
    img.setAttribute("loading", "eager");
    img.setAttribute("decoding", "sync");
    if (!isLoaded(img)) pending.push(img);
  }

  const alreadyLoaded = images.length - pending.length;
  const base: PrintImagePreloadResult = {
    total: images.length,
    alreadyLoaded,
    pending: pending.length,
    loaded: 0,
    failed: 0,
    timedOut: false,
  };

  if (pending.length === 0) return base;

  onStart?.(pending.length);

  let settled = 0;
  let loaded = 0;
  let failed = 0;

  const waitFor = (img: HTMLImageElement) =>
    new Promise<void>((resolve) => {
      const done = (ok: boolean) => {
        if (ok) loaded += 1;
        else failed += 1;
        settled += 1;
        onProgress?.(settled, pending.length);
        resolve();
      };

      // Re-check before subscribing. A raster can already be terminal here: one
      // that failed is `complete` with naturalWidth 0 (so the scan above put it
      // in `pending`), and taking an image off lazy loading can complete it from
      // cache between that scan and this listener pass. Either way its
      // load/error has already fired, and waiting for another would hang until
      // the timeout — one broken image stalling every print.
      if (img.complete) {
        done(img.naturalWidth > 0);
        return;
      }

      img.addEventListener("load", () => done(true), { once: true });
      img.addEventListener("error", () => done(false), { once: true });
    });

  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<"timeout">((resolve) => {
    timer = setTimeout(() => resolve("timeout"), timeoutMs);
  });

  const outcome = await Promise.race([
    Promise.all(pending.map(waitFor)).then(() => "settled" as const),
    timeout,
  ]);
  if (timer) clearTimeout(timer);

  return { ...base, loaded, failed, timedOut: outcome === "timeout" };
};
